/*
 This file is part of GNU Taler
 (C) 2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU Affero Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU Affero Public License for more details.

 You should have received a copy of the GNU Affero Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { format, intervalToDuration, isFuture, isValid, parse } from "date-fns";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_1_officer = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Identification Form (acceptance)",
    de: "Identifikationsformular (Annahme)",
  }),
  description: i18n.fixed({
    en: "The customer has to be identified on entering into a permanent business relationship or on concluding a cash transaction, which meets the according threshold.",
    de: "Die Vertragspartei muss identifiziert werden bei allen dauernden Geschäftsbeziehungen sowie bei Kassageschäften, bei welchen der entsprechende Schwellenwert erreicht wird.",
  }),
  id: "vqf_902_1_officer",
  version: 1,
  config: VQF_902_1_officer(i18n),
});

/**
 * Design of the vqf_902_1_officer form.
 */
export function VQF_902_1_officer(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  const today = format(new Date(), "yyyy-MM-dd");

  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    sections: [
      {
        title: i18n.fixed({
          en: "Acceptance of business relationship",
          de: "Annahme der Geschäftsbeziehung",
        }),
        fields: [
          {
            id: TalerFormAttributes.ACCEPTANCE_DATE,
            label: i18n.fixed({
              en: "Date (conclusion of contract):",
              de: "Datum (Vertragsabschluss):",
            }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            defaultValue: today,
            disabled: true,
            required: true,
          },
          {
            id: TalerFormAttributes.ACCEPTANCE_METHOD,
            label: i18n.fixed({ en: "Accepted via:", de: "Angenommen über:" }),
            type: "choiceStacked",
            choices: [
              {
                value: "FACE_TO_FACE",
                label: i18n.fixed({ en: "Face to face", de: "Persönlich" }),
              },
              {
                value: "AUTHENTICATED_COPY",
                label: i18n.fixed({
                  en: "Authenticated copy of identification document",
                  de: "Beglaubigte Kopie des Ausweisdokuments",
                }),
              },
              {
                value: "RESIDENTIAL_ADDRESS_VALIDATED",
                label: i18n.fixed({
                  en: "Residential address validated",
                  de: "Wohnsitzadresse überprüft",
                }),
              },
            ],
            required: true,
          },
          {
            id: TalerFormAttributes.ACCEPTANCE_FURTHER_INFO,
            label: i18n.fixed({
              en: "Further information:",
              de: "Weitere Angaben:",
            }),
            type: "textArea",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Evaluation with regard to embargo procedures/terrorism lists on establishing the business relationship",
          de: "Prüfung im Hinblick auf Embargomassnahmen/Terrorlisten bei Aufnahme der Geschäftsbeziehung",
        }),
        description: i18n.fixed({
          en: "Verification whether the customer, beneficial owners of the assets, controlling persons, authorized representatives or other involved persons are listed on an embargo/terrorism list.",
          de: "Überprüfung, ob der Kunde, die wirtschaftlich Berechtigten der Vermögenswerte, die Kontrollinhaber, bevollmächtigten Vertreter oder andere beteiligte Personen auf einer Embargo-/Terrorliste aufgeführt sind.",
        }),
        fields: [
          {
            id: TalerFormAttributes.EMBARGO_TERRORISM_CHECK_RESULT,
            label: i18n.fixed({
              en: "Embargo/terrorism status:",
              de: "Embargo-/Terror-Status:",
            }),
            type: "choiceStacked",
            required: true,
            choices: [
              {
                label: i18n.fixed({
                  en: "Not listed on embargo/terrorism list.",
                  de: "Nicht auf einer Embargo-/Terrorliste aufgeführt.",
                }),
                value: "NOT_LISTED",
              },
              {
                label: i18n.fixed({
                  en: "Listed on embargo/terrorism list.",
                  de: "Auf einer Embargo-/Terrorliste aufgeführt.",
                }),
                value: "LISTED",
              },
            ],
          },
          {
            id: TalerFormAttributes.EMBARGO_TERRORISM_INFO,
            label: i18n.fixed({
              en: "Embargo/terrorism information:",
              de: "Embargo-/Terror-Angaben:",
            }),
            type: "textArea",
            hide(value, root): boolean {
              return (
                root[TalerFormAttributes.EMBARGO_TERRORISM_CHECK_RESULT] !==
                "LISTED"
              );
            },
            required: true,
          },
          {
            id: TalerFormAttributes.EMBARGO_TERRORISM_CHECK_DATE,
            label: i18n.fixed({
              en: "Verification date",
              de: "Datum der Überprüfung",
            }),
            type: "isoDateText",
            required: true,
            defaultValue: today,
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            validator(text, form) {
              //FIXME: why returning in this format even if pattern is in another?
              const time = parse(text, "yyyy-MM-dd", new Date());
              if (!isValid(time)) {
                return i18n.fixed({
                  en: "invalid format",
                  de: "ungültiges Format",
                });
              }
              if (isFuture(time)) {
                return i18n.fixed({
                  en: "it can't be in the future",
                  de: "darf nicht in der Zukunft liegen",
                });
              }
              const { years } = intervalToDuration({
                start: time,
                end: new Date(),
              });
              if (years && years > 120) {
                return i18n.fixed({
                  en: "it can't be greater than 120 years",
                  de: "darf nicht größer als 120 Jahre sein",
                });
              }
              return undefined;
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Supplemental File Upload",
          de: "Hochladen zusätzlicher Dokumente",
        }),
        description: i18n.fixed({
          en: "Optional supplemental information for the establishment of the business relationship with the customer.",
          de: "Optionale zusätzliche Informationen zur Begründung der Geschäftsbeziehung mit dem Kunden.",
        }),
        fields: [
          {
            id: TalerFormAttributes.SUPPLEMENTAL_FILES_LIST,
            label: i18n.fixed({
              en: "Supplemental files",
              de: "Ergänzende Dokumente",
            }),
            type: "array",
            labelFieldId: "FILE.FILENAME",
            required: false,
            fields: [
              {
                id: TalerFormAttributes.DESCRIPTION,
                label: i18n.fixed({ en: "Description", de: "Beschreibung" }),
                type: "textArea",
                required: true,
              },
              {
                id: TalerFormAttributes.FILE,
                label: i18n.fixed({ en: "File (PDF)", de: "Datei (PDF)" }),
                type: "file",
                accept: "application/pdf",
                required: true,
              },
            ],
          },
        ],
      },
    ],
  };
}
